import { useEffect, useMemo, useState } from 'react';
import newsHero1 from './assets/NUHero_Students.jpg';
import newsHero2 from './assets/NUHero_Teacher.jpg';
import newsHero3 from './assets/NUHero_Workshop.jpg';
import newsCard1 from './assets/news/news3.jpg';
import newsCard2 from './assets/news/news4.jpg';
import newsCard3 from './assets/news/news7.jpg';
import pressReleaseCommunity from './assets/PressRelease_Community.png';
import pressReleasePartnership from './assets/PressRelease_Partnership.jpg';
import blogArticleEducation from './assets/BlogArticle_Education.jpg';
import blogArticleStudents from './assets/BlogArticle_Students.jpg';
import blogArticleTeacher from './assets/BlogArticle_Teacher.jpg';

function NewsUpdatesPage({ page, onNavigate }) {
  const heroSlides = [newsHero1, newsHero2, newsHero3];
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.news-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const heroTitle = page?.title || 'News & Updates';
  const heroSummary =
    page?.summary ||
    'Stay informed with the latest announcements, campus events, and development updates.';

  const fallbackImages = useMemo(() => [newsCard1, newsCard2, newsCard3], []);

  const defaultPressReleases = [
    {
      id: 'community-involvement',
      title: 'Community Involvement',
      description:
        'Highlights of outreach programs, service learning, and local collaborations led by students and staff.',
      image: pressReleaseCommunity,
    },
    {
      id: 'partnerships',
      title: 'Partnerships',
      description:
        'Updates on strategic partnerships that strengthen learning, facilities, and student opportunities.',
      image: pressReleasePartnership,
    },
  ];

  const rawPressReleases = defaultPressReleases;
  const pressReleases = useMemo(
    () =>
      rawPressReleases.map((item, index) => {
        if (typeof item === 'string') {
          return {
            id: `press-${index + 1}`,
            title: item,
            description: 'Press release details will be updated soon.',
            image: fallbackImages[index % fallbackImages.length],
          };
        }

        const title = item?.title || `Press Release ${index + 1}`;
        const normalizedId = String(item?.id || title)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');

        return {
          id: normalizedId || `press-${index + 1}`,
          title,
          description: item?.description || 'Press release details will be updated soon.',
          image: item?.image || fallbackImages[index % fallbackImages.length],
        };
      }),
    [rawPressReleases, fallbackImages]
  );

  const defaultBlogArticles = [
    {
      id: 'education-trends',
      title: 'Education Trends',
      description:
        'Insights on emerging learning approaches, curriculum updates, and global education trends.',
      image: blogArticleEducation,
    },
    {
      id: 'student-highlights',
      title: 'Student Highlights',
      description:
        'Stories of student achievements, creativity, leadership, and school life moments.',
      image: blogArticleStudents,
    },
    {
      id: 'teacher-insights',
      title: 'Teacher Insights',
      description:
        'Perspective pieces from our educators about classroom experiences and learning journeys.',
      image: blogArticleTeacher,
    },
  ];

  const rawBlogArticles = defaultBlogArticles;
  const blogArticles = useMemo(
    () =>
      rawBlogArticles.map((item, index) => {
        if (typeof item === 'string') {
          return {
            id: `article-${index + 1}`,
            title: item,
            description: 'Article details will be updated soon.',
            image: fallbackImages[index % fallbackImages.length],
          };
        }

        const title = item?.title || `Article ${index + 1}`;
        const normalizedId = String(item?.id || title)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');

        return {
          id: normalizedId || `article-${index + 1}`,
          title,
          description: item?.description || 'Article details will be updated soon.',
          image: item?.image || fallbackImages[index % fallbackImages.length],
        };
      }),
    [rawBlogArticles, fallbackImages]
  );

  return (
    <section className="news-updates-page">
      <section className="admissions-hero" id="news-overview">
        <div className="admissions-hero-media" aria-hidden="true">
          {heroSlides.map((slide, index) => (
            <div
              key={slide}
              className={`admissions-hero-slide ${index === activeSlide ? 'active' : ''}`}
            >
              <img src={slide} alt="News hero" />
            </div>
          ))}
        </div>
        <div className="admissions-hero-overlay" aria-hidden="true" />
        <div className="admissions-hero-content">
          <p className="admissions-hero-kicker">News & Updates</p>
          <h2>{heroTitle}</h2>
          <p>{heroSummary}</p>
          <div className="admissions-hero-dots" role="tablist" aria-label="News hero slides">
            {heroSlides.map((slide, index) => (
              <button
                key={slide}
                type="button"
                className={`admissions-hero-dot ${index === activeSlide ? 'active' : ''}`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="news-press" id="press-releases">
        <div className="news-press-container">
          <div className="news-press-header">
            <p className="news-press-kicker">Press Releases</p>
            <h3>Community Involvement & Partnerships</h3>
            <p>
              Official updates on our outreach programs, strategic collaborations, and community initiatives that strengthen our school and local community.
            </p>
          </div>

          <div className="news-press-grid">
            {pressReleases.map((item, index) => (
              <article
                key={item.id}
                id={item.id}
                className="news-press-card news-animate"
                onClick={() => onNavigate && onNavigate(`news-press-${item.id}`)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onNavigate && onNavigate(`news-press-${item.id}`);
                  }
                }}
              >
                <div className="news-press-card-media">
                  <img src={item.image} alt={item.title} />
                  <div className="news-press-card-overlay" />
                  <div className="news-press-card-badge">0{index + 1}</div>
                </div>
                <div className="news-press-card-body">
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                  <div className="news-press-card-footer">
                    <button type="button" className="news-press-card-link">
                      Read More <i className="fas fa-arrow-right" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="news-blog" id="blog-articles">
        <div className="news-blog-header">
          <p className="news-blog-kicker">Blog / Articles</p>
          <h3>Education Trends, Student Highlights & Insights</h3>
          <p>
            Thoughtful reads that reflect learning progress, community voices, classroom experiences, and newsletters from our school community.
          </p>
        </div>

        <div className="news-blog-magazine">
          {blogArticles.map((item, index) => (
            <article
              key={item.id}
              id={item.id}
              className="news-blog-feature news-animate"
              onClick={() => onNavigate && onNavigate(`news-blog-${item.id}`)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onNavigate && onNavigate(`news-blog-${item.id}`);
                }
              }}
            >
              <div className="news-blog-feature-media">
                <img src={item.image} alt={item.title} />
              </div>
              <div className="news-blog-feature-body">
                <p className="news-blog-tag">{index === 0 ? 'Featured' : 'Article'}</p>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

export default NewsUpdatesPage;
